// One supplier's part of one RFQ, opened beside the RFQ Center list: a short
// summary, the supplier's latest message, the suggested next moves, and the
// conversation. The bar on top follows the view the buyer came from.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/ConversationPanel.tsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Collapse, Stack, TextField, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { AttachFile as AttachIcon, AutoAwesome as SuggestedIcon, ChevronLeft, ChevronRight, Close as CloseIcon, ExpandLess as LessIcon, ExpandMore as MoreIcon, OpenInFull as ExpandIcon, CloseFullscreen as CollapseIcon, Reply as ReplyIcon, Shortcut as ForwardIcon } from '@mui/icons-material';
import buyerAgentService, { CommunicationThread, RfqDocument, SupplierQuote, ThreadMessage } from '../../../services/buyerAgentService';
import { DraftPreviewPanel, DraftPreviewPanelHandle } from './RfqDraftComposer';
import QuoteSteps from './QuoteSteps';
import rfqCenterService, { CenterRow, CenterSuggestion, TimelineEntry } from '../../../services/rfqCenterService';
import MessageBubble from '../MessageBubble';
import { CenterTab, TAB_LABEL, askedLine, quoteLine, stateColors } from './centerView';

interface ConversationPanelProps {
  row: CenterRow;
  tab: CenterTab;
  /** The rows the view the buyer came from offers as neighbours, this row included. */
  siblings: CenterRow[];
  /** The RFQ, part or supplier this conversation was opened from, to go back to. */
  backLabel?: string;
  onBack?: () => void;
  expanded: boolean;
  onOpen: (rfqId: string) => void;
  onClose: () => void;
  onToggleExpanded: () => void;
  /** Something was sent or decided: the list needs fresh data. */
  onChanged: () => void;
}

const LABEL_SX = { fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: 'text.disabled' } as const;

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>');
}

function shortDate(iso?: string): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';
}

/** A suggested move, shaped like a mail client's suggested reply: a pill, blue for a suggestion, the agent's first choice outlined. */
const SuggestionPill: React.FC<{ label: string; best?: boolean; plain?: boolean; selected?: boolean; disabled?: boolean; tip?: string; icon?: React.ReactNode; onClick: () => void }> = ({ label, best, plain, selected, disabled, tip, icon, onClick }) => {
  const theme = useTheme();
  const blue = theme.palette.primary.main;
  return (
    <Tooltip title={tip ?? ''} arrow>
      <Box
        component="button"
        type="button"
        disabled={disabled}
        onClick={onClick}
        sx={{
          font: 'inherit',
          fontSize: '0.9rem',
          fontWeight: 600,
          minHeight: 40,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 2.25,
          py: 0.75,
          borderRadius: 999,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          color: plain ? 'text.primary' : blue,
          bgcolor: selected ? alpha(blue, 0.1) : 'background.paper',
          border: `1px solid ${best || selected ? blue : plain ? theme.palette.text.secondary : theme.palette.divider}`,
          transition: 'background-color .12s',
          '&:hover': { bgcolor: disabled ? undefined : alpha(blue, 0.08) },
          '&:focus-visible': { outline: `2px solid ${blue}`, outlineOffset: 2 },
        }}
      >
        {best && <SuggestedIcon sx={{ fontSize: 16 }} />}
        {icon}
        {label}
      </Box>
    </Tooltip>
  );
};

const KIND_TAG: Record<TimelineEntry['kind'], { label: string; color: string; bg: string }> = {
  supplier: { label: 'Supplier', color: '#374151', bg: '#F1F3F5' },
  sent: { label: 'Sent', color: '#1D4ED8', bg: '#DBEAFE' },
  automatic: { label: 'Automatic', color: '#9CA3AF', bg: '#F3F4F6' },
};

const ConversationPanel: React.FC<ConversationPanelProps> = ({ row, tab, siblings, backLabel, onBack, expanded, onOpen, onClose, onToggleExpanded, onChanged }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [thread, setThread] = useState<CommunicationThread | null>(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  /** The lines of the conversation opened to their full message. */
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const [showOriginal, setShowOriginal] = useState(false);
  // Discarding asks once, in place: the button turns into "Discard" and "Keep".
  const [discarding, setDiscarding] = useState<'ask' | 'busy' | null>(null);
  useEffect(() => setDiscarding(null), [row.rfqId]);
  const discard = async () => {
    setDiscarding('busy');
    try {
      await buyerAgentService.discardRfqDraft(row.rfqId);
      onChanged();
      (onBack ?? onClose)();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e?.response?.data?.message || e?.message || 'Could not discard the draft');
      setDiscarding(null);
    }
  };
  const [overviewOpen, setOverviewOpen] = useState(true);
  const [forwardTo, setForwardTo] = useState('');
  /** A draft is reviewed and sent right here: the full RFQ, and whether it can go. */
  const [draftRfq, setDraftRfq] = useState<RfqDocument | null>(null);
  const [sendable, setSendable] = useState({ canSend: false, sending: false });
  const draftRef = useRef<DraftPreviewPanelHandle>(null);
  const isDraft = row.state === 'draft';

  /** The RFQ's quotes: each is shown under the email it was read from. */
  const [quotes, setQuotes] = useState<SupplierQuote[]>([]);

  const loadThread = useCallback(async () => {
    if (!row.threadId) {
      setThread(null);
      setTimeline([]);
      setQuotes([]);
      return;
    }
    setLoading(true);
    try {
      const [full, lines, rfq] = await Promise.all([
        buyerAgentService.getThread(row.threadId),
        rfqCenterService.getTimeline(row.rfqId),
        buyerAgentService.getRfq(row.rfqId).catch(() => null),
      ]);
      setThread(full);
      setTimeline(lines);
      setQuotes(rfq?.quotes ?? []);
    } catch {
      setThread(null);
      setTimeline([]);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [row.threadId, row.rfqId]);

  useEffect(() => {
    setPicked(null);
    setDraft('');
    setError(null);
    setDone(null);
    setOpened(new Set());
    setShowOriginal(false);
    setOverviewOpen(true);
    setForwardTo('');
    setDraftRfq(null);
    if (row.state === 'draft') void buyerAgentService.getRfq(row.rfqId).then(setDraftRfq).catch(() => setDraftRfq(null));
    void loadThread();
  }, [row.rfqId, row.state, loadThread]);

  /** The quote read from a message: by the mail it came in, else by its file being one of the message's attachments. */
  const quoteOf = (m: ThreadMessage): SupplierQuote | undefined =>
    [...quotes].reverse().find(
      (q) =>
        (!!q.sourceMessageId && (q.sourceMessageId === m.gmailMessageId || q.sourceMessageId === m.messageId)) ||
        (q.documents ?? []).some((d) => (m.attachments ?? []).some((a) => a.documentId === d.documentId)),
    );
  const bubble = (m: ThreadMessage) => {
    const q = m.direction === 'INBOUND' ? quoteOf(m) : undefined;
    return <MessageBubble message={m} threadId={row.threadId ?? undefined} footer={q ? <QuoteRead quote={q} threadId={row.threadId ?? undefined} /> : undefined} />;
  };

  const index = siblings.findIndex((s) => s.rfqId === row.rfqId);
  const messageById = useMemo(() => new Map((thread?.messages ?? []).map((m) => [m.messageId, m])), [thread]);
  const original = row.latest?.messageId ? messageById.get(row.latest.messageId) : undefined;
  const toggle = (id: string) =>
    setOpened((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const colors = stateColors(row);
  const canWrite = !!row.threadId && row.state !== 'draft' && row.state !== 'closed';

  /** Whoever wrote last for the supplier, else the address the RFQ went to. */
  const replyTo = useMemo(() => {
    if (row.nowWith?.email) return { name: row.nowWith.name, email: row.nowWith.email };
    const sent = thread?.messages?.find((m) => m.direction === 'OUTBOUND');
    const first = sent?.recipients?.[0];
    return first ? { name: first.name ?? row.supplier.name, email: first.email } : null;
  }, [row.nowWith, row.supplier.name, thread]);

  const choose = async (s: CenterSuggestion) => {
    setError(null);
    if (s.kind === 'review-draft') return;
    if (s.kind === 'accept-quote') {
      if (!s.quoteId) return;
      setBusy(true);
      try {
        await buyerAgentService.acceptQuote(row.rfqId, s.quoteId);
        setDone('Quote accepted.');
        onChanged();
      } catch (e) {
        setError((e as Error).message || 'The quote could not be accepted.');
      } finally {
        setBusy(false);
      }
      return;
    }
    setPicked(picked === s.id ? null : s.id);
    setDraft(s.text ?? '');
  };

  const send = async () => {
    const forwarding = picked === 'forward';
    const to = forwarding ? (forwardTo.trim() ? { name: '', email: forwardTo.trim() } : null) : replyTo;
    if (!row.threadId || !to || !draft.trim()) return;
    setBusy(true);
    setError(null);
    try {
      // The subject of the mails themselves, as a mail program answers them: the thread's own name is internal.
      const subject = [...(thread?.messages ?? [])].reverse().find((m) => m.subject)?.subject ?? thread?.subject ?? `RFQ ${row.rfqNumber}`;
      const result = await buyerAgentService.sendMessage(row.threadId, {
        subject: forwarding ? `Fwd: ${subject.replace(/^(re|aw|fwd?):\s*/i, '')}` : /^(re|aw):/i.test(subject) ? subject : `Re: ${subject}`,
        bodyPlaintext: draft,
        bodyHtml: escapeHtml(draft),
        recipients: [{ name: to.name, email: to.email, type: 'TO' }],
      });
      if (result?.deliveryStatus === 'FAILED') {
        setError('The mail was not sent. Check the mailbox settings and try again.');
        return;
      }
      setPicked(null);
      setDraft('');
      setDone(`Sent to ${to.name || to.email}.`);
      setForwardTo('');
      await loadThread();
      onChanged();
    } catch (e) {
      setError((e as Error).message || 'The mail was not sent.');
    } finally {
      setBusy(false);
    }
  };

  const waiting = row.waitingOn;
  const sentCells: Array<{ label: string; value: string; tip?: string; hot?: boolean; tag?: string }> = [
    { label: row.state === 'draft' ? 'To ask' : 'Asked', value: askedLine(row), tip: [row.lines[0]?.description, row.sentAt ? `Sent ${shortDate(row.sentAt)}` : ''].filter(Boolean).join('. ') },
    { label: 'Now with', value: row.nowWith?.name ?? row.supplier.name, tip: row.nowWith?.email, tag: row.nowWith?.isNew ? 'New contact' : undefined },
    { label: 'Quote', value: quoteLine(row), tip: row.quote ? `${row.quote.count} ${row.quote.count === 1 ? 'quote' : 'quotes'}${row.quote.moq ? `, minimum order ${row.quote.moq}` : ''}` : undefined, tag: row.quote?.reviewStatus === 'PENDING' ? 'To review' : undefined },
    {
      label: 'Waiting on',
      value: waiting.who === 'nobody' ? 'Nobody' : `${waiting.who === 'us' ? 'Us' : 'Supplier'}${waiting.days !== undefined ? ` · ${waiting.days} ${waiting.days === 1 ? 'day' : 'days'}` : ''}`,
      tip: waiting.detail,
      hot: waiting.who === 'us' || row.stale,
    },
  ];
  const cells = sentCells;

  return (
    <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 2, minWidth: 0 }}>
      {/* Where the buyer came from, and the neighbours that view offers */}
      <Stack direction="row" spacing={1} sx={{ px: 2.5, py: 1, alignItems: 'center', flexWrap: 'wrap', rowGap: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        {onBack ? (
          <Button size="small" startIcon={<ChevronLeft />} onClick={onBack} sx={{ textTransform: 'none', fontWeight: 700, color: 'text.primary', maxWidth: 260 }}>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {backLabel ?? TAB_LABEL[tab]}
            </Box>
          </Button>
        ) : (
          <Typography variant="body2" fontWeight={700}>
            {TAB_LABEL[tab]}
          </Typography>
        )}
        {index >= 0 && siblings.length > 1 && (
          <>
            <Typography variant="caption" color="text.secondary">
              {index + 1} of {siblings.length}
            </Typography>
            <Button size="small" startIcon={<ChevronLeft />} disabled={index <= 0} onClick={() => onOpen(siblings[index - 1].rfqId)}>
              Previous
            </Button>
            <Button size="small" endIcon={<ChevronRight />} disabled={index >= siblings.length - 1} onClick={() => onOpen(siblings[index + 1].rfqId)}>
              Next
            </Button>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        {isDraft &&
          (discarding ? (
            <>
              <Typography variant="caption" color="text.secondary">
                Discard this draft?
              </Typography>
              <Button size="small" color="error" disabled={discarding === 'busy'} onClick={discard} sx={{ textTransform: 'none' }}>
                Discard
              </Button>
              <Button size="small" disabled={discarding === 'busy'} onClick={() => setDiscarding(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                Keep
              </Button>
            </>
          ) : (
            <Button size="small" onClick={() => setDiscarding('ask')} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Discard draft
            </Button>
          ))}
        <Button size="small" onClick={() => navigate(`/rfq/${row.rfqId}/workspace`)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Supplier Space
        </Button>
        <Tooltip title={expanded ? 'Show the list' : 'Full width'}>
          <Button size="small" onClick={onToggleExpanded} sx={{ minWidth: 0, color: 'text.secondary' }} aria-label={expanded ? 'Show the list' : 'Full width'}>
            {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </Button>
        </Tooltip>
        {/* Full width, the back link already leaves this RFQ. */}
        {!(expanded && onBack) && (
          <Button size="small" onClick={onClose} startIcon={<CloseIcon fontSize="small" />} sx={{ color: 'text.secondary' }}>
            Close
          </Button>
        )}
      </Stack>

      {/* A draft's name and steps line up with the text inside the email panel below them. */}
      <Box sx={{ px: isDraft ? 5.5 : 2.5, pt: 2.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
          {row.supplier.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.rfqNumber}
          {row.referredBy ? ` · Referred by ${row.referredBy}` : ''}
          {isDraft ? ' · Draft, not sent yet' : ''}
        </Typography>
      </Box>

      {/* A draft is the last step of getting a quote; the email below says the rest. */}
      {isDraft ? (
        <Box sx={{ mx: 5.5, mt: 2 }}>
          <QuoteSteps
            current={3}
            onGo={{
              1: () => navigate(`/rfq/new?draft=${encodeURIComponent(row.rfqId)}&step=1`),
              2: () => navigate(`/rfq/new?draft=${encodeURIComponent(row.rfqId)}&step=2`),
            }}
          />
        </Box>
      ) : (
        // Summary: one strip, the same four cells for every conversation
        <Box sx={{ mx: 2.5, mt: 1.25, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, minmax(0, 1fr))' }, overflow: 'hidden' }}>
          {cells.map((c) => (
            <Tooltip key={c.label} title={c.tip ?? ''} placement="top" arrow>
              <Box sx={{ px: 1.75, py: 1.25, minWidth: 0, borderRight: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}`, mr: '-1px', mb: '-1px' }}>
                <Typography sx={LABEL_SX}>{c.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere', color: c.hot ? theme.palette.error.main : 'text.primary' }}>
                  {c.value}
                </Typography>
                {c.tag && (
                  <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.warning.dark }}>
                    {c.tag}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}

      {isDraft ? (
        /* A draft: the RFQ as it will go out, edited and sent from here */
        <Box sx={{ mx: 2.5, mt: 3.5, mb: 2.5 }}>
          {done && <Alert severity="success" sx={{ mb: 1.5 }}>{done}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          {draftRfq ? (
            <DraftPreviewPanel
              ref={draftRef}
              rfq={draftRfq}
              onSendableChange={setSendable}
              onSent={() => {
                setDone(`Sent to ${row.supplier.name}.`);
                onChanged();
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {/* The last step, after everything it sends; it stays in view while the draft is read */}
          <Stack direction="row" spacing={1.5} sx={{ position: 'sticky', bottom: 12, mt: 2, alignItems: 'center', bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 999, px: 1, py: 0.75, boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.08)}`, width: 'fit-content', maxWidth: '100%' }}>
            <SuggestionPill label={sendable.sending ? 'Sending…' : `Send to ${row.supplier.name}`} best disabled={!sendable.canSend || sendable.sending} tip={!sendable.canSend ? 'Add a recipient above first' : undefined} onClick={() => draftRef.current?.send()} />
            <Typography variant="caption" color="text.secondary" sx={{ pr: 1.5 }}>
              Sends the mail above as shown.
            </Typography>
          </Stack>
        </Box>
      ) : (
        <>
      {/* The supplier's latest message, then the suggested next moves */}
      <Stack spacing={1.5} sx={{ mx: 2.5, mt: 1.75 }}>
        {/* Overview: the conversation in a few bullets, as a mail client shows its summary */}
        <Box sx={{ borderRadius: 3, px: 2.25, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
          <Box component="button" type="button" onClick={() => setOverviewOpen((v) => !v)} aria-expanded={overviewOpen} sx={{ font: 'inherit', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center', gap: 1, border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', textAlign: 'left' }}>
            <SuggestedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
              {row.overview.byAgent ? 'AI overview' : 'Overview'}
            </Typography>
            {overviewOpen ? <LessIcon sx={{ fontSize: 20, color: 'text.secondary' }} /> : <MoreIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
          </Box>
          <Collapse in={overviewOpen}>
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {row.overview.points.map((point, n) => (
                <Typography key={n} component="li" dir="auto" variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {point}
                </Typography>
              ))}
            </Box>
            {original && (
              <Box sx={{ mt: 1 }}>
                <Button size="small" onClick={() => setShowOriginal((v) => !v)} endIcon={<MoreIcon sx={{ transform: showOriginal ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />} sx={{ textTransform: 'none', fontWeight: 600, px: 0.5 }}>
                  {showOriginal ? 'Hide original message' : 'Original message'}
                </Button>
                <Collapse in={showOriginal} unmountOnExit>
                  <Box sx={{ mt: 1, bgcolor: 'background.paper', borderRadius: 2, p: 1.5 }}>
                    {bubble(original)}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Collapse>
        </Box>

        {done && <Alert severity="success">{done}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}

        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          {row.suggestions.map((s) => (
            <SuggestionPill key={s.id} label={s.label} best={s.best} selected={picked === s.id} disabled={busy} tip={s.best && s.why ? `Suggested: ${s.why}` : undefined} onClick={() => void choose(s)} />
          ))}
          {canWrite && (
            <SuggestionPill
              label="Reply"
              plain
              icon={<ReplyIcon sx={{ fontSize: 18 }} />}
              selected={picked === 'own'}
              disabled={busy}
              onClick={() => {
                setPicked(picked === 'own' ? null : 'own');
                setDraft('');
              }}
            />
          )}
          {canWrite && (
            <SuggestionPill
              label="Forward"
              plain
              icon={<ForwardIcon sx={{ fontSize: 18 }} />}
              selected={picked === 'forward'}
              disabled={busy}
              onClick={() => {
                setPicked(picked === 'forward' ? null : 'forward');
                setDraft(original ? `\n\n---------- Forwarded message ----------\nFrom: ${original.sender?.name || original.sender?.email || row.supplier.name}\nSubject: ${original.subject ?? ''}\n\n${original.bodyPlaintext ?? row.latest?.text ?? ''}` : '');
              }}
            />
          )}
        </Stack>

        {picked && (
          <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 1.5, boxShadow: `0 2px 10px ${alpha(theme.palette.common.black, 0.06)}` }}>
            {picked === 'forward' ? (
              <TextField id={`forward-to-${row.rfqId}`} size="small" variant="standard" fullWidth type="email" placeholder="Forward to (email address)" value={forwardTo} onChange={(e) => setForwardTo(e.target.value)} />
            ) : (
              <Typography variant="caption" color="text.secondary">
                {replyTo ? `To ${replyTo.name || replyTo.email}` : 'No address to reply to'}
              </Typography>
            )}
            <TextField
              id={`reply-${row.rfqId}`}
              multiline
              minRows={picked === 'own' ? 4 : 6}
              fullWidth
              variant="standard"
              placeholder="Write to the supplier"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              InputProps={{ disableUnderline: true }}
              inputProps={{ dir: 'auto' }}
              sx={{ mt: 0.5 }}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
              <Button variant="contained" size="small" disabled={busy || !draft.trim() || (picked === 'forward' ? !/^\S+@\S+\.\S+$/.test(forwardTo.trim()) : !replyTo)} onClick={() => void send()}>
                Send
              </Button>
              <Button size="small" disabled={busy} onClick={() => setPicked(null)}>
                Discard
              </Button>
            </Stack>
          </Box>
        )}
      </Stack>

      {/* The conversation, clearly below */}
      <Box sx={{ mt: 3.5, borderTop: `1px solid ${theme.palette.divider}`, px: 2.5, pt: 2, pb: 2.5 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
          <Typography sx={LABEL_SX}>Conversation</Typography>
          <Box sx={{ px: 0.75, borderRadius: 1, bgcolor: colors.bg, color: colors.color, fontSize: '0.7rem', fontWeight: 600 }}>{row.chip}</Box>
        </Stack>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : timeline.length ? (
          <Stack spacing={1}>
            {timeline.map((line, n) => {
              const id = line.messageId ?? String(n);
              const full = line.messageId ? messageById.get(line.messageId) : undefined;
              const isOpen = opened.has(id);
              const tag = KIND_TAG[line.kind];
              const muted = line.kind === 'automatic';
              return (
                <Box key={id} sx={{ border: `1px solid ${isOpen ? theme.palette.primary.main : theme.palette.divider}`, borderRadius: 2.5, bgcolor: 'background.paper', overflow: 'hidden', transition: 'box-shadow .12s, border-color .12s', '&:hover': { boxShadow: `0 2px 10px ${alpha(theme.palette.common.black, 0.06)}` } }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => toggle(id)}
                    aria-expanded={isOpen}
                    sx={{
                      font: 'inherit',
                      color: 'inherit',
                      width: '100%',
                      textAlign: 'left',
                      border: 0,
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                      display: 'grid',
                      gridTemplateColumns: '40px minmax(0, 1fr) auto',
                      gap: 1.75,
                      alignItems: 'center',
                      px: 2,
                      py: 1.5,
                      opacity: muted ? 0.65 : 1,
                      '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
                    }}
                  >
                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: '0.9rem', color: tag.color, bgcolor: tag.bg }}>{(line.who.trim()[0] ?? '?').toUpperCase()}</Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {line.who}
                        </Typography>
                        <Box component="span" sx={{ px: 0.75, borderRadius: 0.75, fontSize: '0.68rem', fontWeight: 600, color: tag.color, bgcolor: tag.bg, whiteSpace: 'nowrap' }}>
                          {tag.label}
                        </Box>
                      </Stack>
                      <Typography dir="auto" variant="body2" color="text.secondary" sx={{ mt: 0.25, display: '-webkit-box', WebkitLineClamp: isOpen ? 1 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere' }}>
                        {line.snippet || 'No text'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                      {line.attachments > 0 && (
                        <Stack direction="row" sx={{ alignItems: 'center' }}>
                          <AttachIcon sx={{ fontSize: 15 }} />
                          <Typography variant="caption">{line.attachments}</Typography>
                        </Stack>
                      )}
                      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {shortDate(line.when)}
                      </Typography>
                      <MoreIcon sx={{ fontSize: 20, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                    </Stack>
                  </Box>
                  <Collapse in={isOpen} unmountOnExit>
                    <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: `1px solid ${theme.palette.divider}` }}>{full ? bubble(full) : <Typography variant="body2" color="text.secondary">{line.snippet}</Typography>}</Box>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nothing sent yet.
          </Typography>
        )}
      </Box>
        </>
      )}
    </Box>
  );
};


/** What was read from a supplier's email: the numbers as the quote holds them, and the file they came from. */
const QuoteRead: React.FC<{ quote: SupplierQuote; threadId?: string }> = ({ quote, threadId }) => {
  const theme = useTheme();
  const money = (n?: number, cur?: string) => (n == null ? null : `${cur && cur !== 'USD' ? `${cur} ` : '$'}${n.toLocaleString(undefined, { maximumFractionDigits: 4 })}`);
  const from = quote.sourceType === 'PDF_ATTACHMENT' ? quote.sourceAttachmentFilename ?? 'the attached file' : quote.sourceType === 'EMAIL_BODY' ? 'the email' : 'the quote entered';
  const status = quote.reviewStatus === 'PENDING' ? 'To review' : quote.reviewStatus === 'REJECTED' ? 'Rejected' : 'Accepted';
  const file = (quote.documents ?? [])[0];
  return (
    <Box sx={{ mt: 1.5, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 1.75, py: 1.25, bgcolor: alpha(theme.palette.success.main, 0.04) }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography sx={LABEL_SX}>Read from {quote.sourceType === 'PDF_ATTACHMENT' ? 'the file' : 'this email'}</Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, color: quote.reviewStatus === 'PENDING' ? theme.palette.warning.dark : 'text.secondary' }}>
          {status}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {file && threadId ? (
          <Button size="small" onClick={() => void buyerAgentService.openThreadAttachment(threadId, file.documentId, file.mimeType)} sx={{ textTransform: 'none', p: 0, minWidth: 0, maxWidth: '100%' }}>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Open {file.fileName}</Box>
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{from}</Typography>
        )}
      </Stack>
      {(quote.lineItems ?? []).map((li) => (
        <Box key={li.lineItemId} sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
          {[
            ['Unit price', money(li.quotedUnitPrice, li.quotedCurrency ?? quote.quotedCurrency)],
            ['Lead time', li.quotedLeadTimeDays != null ? `${li.quotedLeadTimeDays} days` : null],
            ['Minimum order', li.quotedMOQ != null ? String(li.quotedMOQ) : null],
            ['Stock', li.stockQuantity != null ? String(li.stockQuantity) : li.stockStatus ?? null],
          ].map(([label, value]) => (
            <Box key={label as string} sx={{ minWidth: 0 }}>
              <Typography sx={LABEL_SX}>{label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value ?? '—'}</Typography>
            </Box>
          ))}
        </Box>
      ))}
      {[quote.deliveryTerms && `Delivery terms: ${quote.deliveryTerms}`, quote.paymentTerms && `Payment: ${quote.paymentTerms}`, quote.validUntil && `Valid until ${new Date(quote.validUntil).toLocaleDateString()}`, quote.notes]
        .filter(Boolean)
        .map((t) => (
          <Typography key={t as string} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
            {t}
          </Typography>
        ))}
    </Box>
  );
};

export default ConversationPanel;
